import { useState, useEffect, useCallback } from 'react';
import { getAllEntrepots, getEntrepotByCode, createEntrepot, updateEntrepot, deleteEntrepot, getDefaultEntrepot, setDefaultEntrepot } from '../models/entrepotAPI';
import { clearDefaultEntrepotCache } from '../utils/defaultEntrepot';

export const useFetchAllEntrepots = () => {
    const [entrepots, setEntrepots] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const fetchEntrepots = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const data = await getAllEntrepots();
            setEntrepots(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchEntrepots();
    }, [fetchEntrepots]);

    return { entrepots, loading, error, refetch: fetchEntrepots };
};

export const useFetchEntrepotByCode = (code_ent) => {
    const [entrepot, setEntrepot] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const fetchEntrepot = useCallback(async () => {
        if (!code_ent) {
            setEntrepot(null);
            return;
        }
        setLoading(true);
        setError('');
        try {
            const data = await getEntrepotByCode(code_ent);
            setEntrepot(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [code_ent]);

    useEffect(() => {
        fetchEntrepot();
    }, [fetchEntrepot]);

    return { entrepot, loading, error, refetch: fetchEntrepot };
};

export const useCreateEntrepot = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const create = useCallback(async (data) => {
        setLoading(true);
        setError('');
        try {
            const result = await createEntrepot(data);
            return result;
        } catch (err) {
            setError(err.response?.data?.message || err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    return { create, loading, error };
};

export const useUpdateEntrepot = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const update = useCallback(async (code_ent, data) => {
        setLoading(true);
        setError('');
        try {
            const result = await updateEntrepot(code_ent, data);
            return result;
        } catch (err) {
            setError(err.response?.data?.message || err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    return { update, loading, error };
};

export const useDeleteEntrepot = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const remove = useCallback(async (code_ent) => {
        setLoading(true);
        setError('');
        try {
            await deleteEntrepot(code_ent);
        } catch (err) {
            setError(err.response?.data?.message || err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    return { remove, loading, error };
};

export const useDefaultEntrepot = () => {
    const [defaultEntrepot, setDefaultEntrepotState] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);

    const fetchDefault = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const data = await getDefaultEntrepot();
            setDefaultEntrepotState(data);
            return data;
        } catch (err) {
            setError(err.response?.data?.message || err.message);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchDefault();
    }, [fetchDefault]);

    const updateDefault = useCallback(async (code_ent) => {
        setSaving(true);
        setError('');
        try {
            const data = await setDefaultEntrepot(code_ent);
            clearDefaultEntrepotCache();
            setDefaultEntrepotState(data);
            return data;
        } catch (err) {
            setError(err.response?.data?.message || err.message);
            throw err;
        } finally {
            setSaving(false);
        }
    }, []);

    return {
        defaultEntrepot,
        defaultCode: defaultEntrepot?.code_ent || null,
        loading,
        saving,
        error,
        refetch: fetchDefault,
        setDefault: updateDefault,
    };
};
